// launchApp：组件型 APP 与外链 APP 的统一启动入口。
// 按 design.md §4.4 分支处理。组件型 APP 会在首次启动时包装成 React.lazy，
// 这样某个 APP 出错时，只会影响该 APP 自己，而不会拖垮整个桌面启动。
import { lazy, type ComponentType } from 'react';
import type { IconSource } from '@blade/os-sdk';
import { useWindowStore } from '../store/windowStore';
import { pushNotification } from '../store/notificationStore';
import { isControlledAppId } from '../license/controlled';
import { isLicenseEnforcedInvalid } from '../license/store';

export interface ResolvedRegistryEntry {
  id: string;
  title: string;
  icon?: IconSource;
  singleton?: boolean;
  defaultSize?: { width: number; height: number };
  load?: () => Promise<ComponentType<unknown>>;
  externalUrl?: string;
  openIn?: 'newTab' | 'sameTab' | 'window';
}

export type RegistryLookup = (appId: string) => ResolvedRegistryEntry | undefined;

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

// 把 externalUrl 解析为当前浏览器可达的地址，匹配"一体机换 IP"部署：
//  1. `{HOST}` 占位符 → 当前浏览器 host（推荐写法，如 `http://{HOST}:8020/`）；
//  2. 写死的 `localhost`/`127.0.0.1` host → 当前浏览器 host。配置里漏写 {HOST}
//     的本地服务（如客服助手 `http://localhost:18080`），从远程 IP 访问 OS 时
//     localhost 会指向用户自己的机器而非盒子，这里按浏览器访问路径推断还原。
// 仅当浏览器自身不是用 loopback 访问 OS 时才重写，避免本机开发场景被破坏。
function resolveExternalUrl(url: string): string {
  if (typeof window === 'undefined') return url;
  const browserHost = window.location.hostname;

  let resolved = url.includes('{HOST}') ? url.replaceAll('{HOST}', browserHost) : url;

  if (!LOOPBACK_HOSTS.has(browserHost)) {
    try {
      const parsed = new URL(resolved);
      if (LOOPBACK_HOSTS.has(parsed.hostname)) {
        parsed.hostname = browserHost;
        resolved = parsed.toString();
      }
    } catch {
      // 非 URL 字符串保持原样；上层已校验 http(s):// 前缀。
    }
  }

  return resolved;
}

type WindowOpenFn = (
  url: string,
  target?: string,
  features?: string,
) => unknown;

type LocationAssignFn = (url: string) => void;

export interface LaunchAppDeps {
  registry: RegistryLookup;
  windowOpen?: WindowOpenFn;
  locationAssign?: LocationAssignFn;
  notify?: (msg: string) => void;
}

export type LaunchAppFn = (
  appId: string,
  options?: { params?: unknown },
) => string | null;

// 按 appId 缓存 lazy 包装器，避免重复启动时每次都重新创建 React.lazy。
// 否则会破坏 Suspense/HMR 边界，并导致 chunk 被重复拉取。
const lazyCache = new Map<string, ComponentType<unknown>>();

function getLazyComponent(
  appId: string,
  load: () => Promise<ComponentType<unknown>>,
): ComponentType<unknown> {
  const cached = lazyCache.get(appId);
  if (cached) return cached;
  const Lazy = lazy(async () => ({ default: await load() }));
  lazyCache.set(appId, Lazy);
  return Lazy;
}

export function resetLaunchAppCache(): void {
  lazyCache.clear();
}

export function makeLaunchApp(deps: LaunchAppDeps): LaunchAppFn {
  const windowOpen = deps.windowOpen ?? ((url, target, features) => window.open(url, target, features));
  const notify = deps.notify ?? ((msg: string) => pushNotification({ message: msg }));

  return function launchApp(
    appId: string,
    options?: { params?: unknown },
  ): string | null {
    const entry = deps.registry(appId);
    if (!entry) {
      notify(`未找到 APP: ${appId}`);
      return null;
    }

    if (isControlledAppId(appId) && isLicenseEnforcedInvalid()) {
      notify('可能无法启动：证书已过期，请激活');
    }

    if (entry.externalUrl) {
      if (!/^https?:\/\//i.test(entry.externalUrl)) {
        notify(`外链 APP ${entry.title} 协议无效`);
        return null;
      }
      // 所有外链 APP 一律在新标签页打开，不在桌面内嵌 iframe，也不在当前页跳转。
      const resolvedUrl = resolveExternalUrl(entry.externalUrl);
      windowOpen(resolvedUrl, '_blank', 'noopener,noreferrer');
      notify(`已在新标签页打开 ${entry.title}`);
      return null;
    }

    if (!entry.load) {
      notify(`APP ${entry.title} 尚未提供组件入口`);
      return null;
    }

    const store = useWindowStore.getState();
    if (entry.singleton) {
      const existing = store.findByAppId(appId);
      if (existing) {
        store.focus(existing.id);
        return existing.id;
      }
    }

    const component = getLazyComponent(entry.id, entry.load);
    return store.openWindow(
      {
        id: entry.id,
        title: entry.title,
        icon: entry.icon,
        singleton: entry.singleton,
        defaultSize: entry.defaultSize,
        component,
      },
      options?.params,
    );
  };
}
